import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const measurements = [
  { size: "XS",  chest: "34–35", waist: "28–29", hip: "36–37", shoulder: "16.5" },
  { size: "S",   chest: "36–37", waist: "30–31", hip: "38–39", shoulder: "17.0" },
  { size: "M",   chest: "38–39", waist: "32–33", hip: "40–41", shoulder: "17.5" },
  { size: "L",   chest: "40–41", waist: "34–35", hip: "42–43", shoulder: "18.0" },
  { size: "XL",  chest: "42–43", waist: "36–37", hip: "44–45", shoulder: "18.5" },
  { size: "XXL", chest: "44–46", waist: "38–40", hip: "46–48", shoulder: "19.0" },
];

const howToMeasure = [
  { label: "Chest",    tip: "Measure around the fullest part of your chest, keeping the tape horizontal." },
  { label: "Waist",    tip: "Measure around your natural waistline, just above the navel." },
  { label: "Hip",      tip: "Stand with feet together. Measure around the fullest part of your hips." },
  { label: "Shoulder", tip: "Measure from the edge of one shoulder seam to the other, across the back." },
];

function ModalContent({ open, onClose }) {
  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    if (open) document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop — rendered into body, above everything */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            style={{ position: "fixed", inset: 0, zIndex: 99999, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(2px)" }}
            aria-hidden="true"
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
            role="dialog"
            aria-modal="true"
            aria-label="Size Guide"
            style={{ position: "fixed", inset: 0, zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ pointerEvents: "auto", maxHeight: "90vh", overflowY: "auto", width: "100%", maxWidth: "680px" }}
              className="bg-[#FAF8F5] rounded-2xl shadow-2xl mx-4"
            >
              {/* Header */}
              <div className="sticky top-0 z-10 flex items-center justify-between bg-[#FAF8F5] border-b border-[#E8E3DA] px-6 py-5">
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#A0916F] font-medium">Atelier</p>
                  <h2 className="font-serif text-2xl text-[#181818] tracking-tight mt-0.5">Size Guide</h2>
                </div>
                <button
                  onClick={onClose}
                  aria-label="Close size guide"
                  className="rounded-full w-9 h-9 flex items-center justify-center border border-[#E8E3DA] text-[#6B6B6B] hover:bg-[#F0EBE3] hover:text-[#181818] transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="px-6 py-6 space-y-8">

                <p className="text-xs text-[#6B6B6B] tracking-wide uppercase">
                  All measurements in inches — body measurements, not garment.
                </p>

                {/* Size table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-[#E8E3DA]">
                        {["Size", "Chest", "Waist", "Hip", "Shoulder"].map((h) => (
                          <th key={h} className="text-left py-3 px-3 text-[10px] tracking-[0.15em] uppercase text-[#6B6B6B] font-semibold first:pl-0">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {measurements.map((row, i) => (
                        <tr key={row.size} className={`border-b border-[#F2EEE8] hover:bg-[#F0EBE3] transition-colors ${i % 2 === 0 ? "bg-white" : "bg-[#FAF8F5]"}`}>
                          <td className="py-3.5 px-3 pl-0 font-semibold text-[#181818] font-serif text-base">{row.size}</td>
                          <td className="py-3.5 px-3 text-[#3A3A3A]">{row.chest}</td>
                          <td className="py-3.5 px-3 text-[#3A3A3A]">{row.waist}</td>
                          <td className="py-3.5 px-3 text-[#3A3A3A]">{row.hip}</td>
                          <td className="py-3.5 px-3 text-[#3A3A3A]">{row.shoulder}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="border-t border-[#E8E3DA]" />

                {/* How to measure */}
                <div>
                  <h3 className="text-[10px] tracking-[0.2em] uppercase text-[#A0916F] font-medium mb-4">How to Measure</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {howToMeasure.map(({ label, tip }) => (
                      <div key={label} className="bg-white border border-[#E8E3DA] rounded-xl px-4 py-4">
                        <p className="font-semibold text-[#181818] text-sm mb-1">{label}</p>
                        <p className="text-xs text-[#6B6B6B] leading-relaxed">{tip}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Fit note */}
                <div className="bg-[#181818] text-[#FAF8F5] rounded-xl px-5 py-4 text-xs leading-relaxed">
                  <span className="font-semibold text-[#C9B48A]">Atelier Fit Note — </span>
                  Our silhouettes are cut slightly relaxed through the chest and shoulder.
                  If you are between sizes or prefer a closer fit, size down.
                  Each garment is hand-inspected and measurements may vary ±0.5″.
                </div>

              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default function SizeGuideModal({ open, onClose }) {
  // Render into document.body so it escapes any stacking context the navbar creates
  return createPortal(
    <ModalContent open={open} onClose={onClose} />,
    document.body
  );
}